import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X } from '@/utils/icons';
import type { TourPlacement, TourStep } from '@/config/tours';

interface TourCoachmarkProps {
  step: TourStep;
  index: number;
  total: number;
  /** Viewport rect of the spotlighted target; null = centered (no target). */
  rect: DOMRect | null;
  onBack: () => void;
  onNext: () => void;
  /** X / Skip — parent shows the skip-all confirmation. */
  onRequestSkip: () => void;
}

const GAP = 12; // distance between target and coachmark
const MARGIN = 8; // minimum distance from viewport edges

/**
 * Compute a fixed-position (top/left) for the coachmark near the target rect,
 * honouring the requested placement and clamping to the viewport. Falls back
 * to the opposite side when the preferred side doesn't fit.
 */
function computePosition(
  rect: DOMRect,
  size: { width: number; height: number },
  placement: TourPlacement,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const centered = {
    top: Math.max(MARGIN, (vh - size.height) / 2),
    left: Math.max(MARGIN, (vw - size.width) / 2),
  };
  if (placement === 'center') return centered;

  // Zero / missing target → center rather than anchoring to (0,0).
  if (rect.width < 2 && rect.height < 2) return centered;

  const fits = {
    top: rect.top - size.height - GAP >= MARGIN,
    bottom: rect.bottom + size.height + GAP <= vh - MARGIN,
    left: rect.left - size.width - GAP >= MARGIN,
    right: rect.right + size.width + GAP <= vw - MARGIN,
  };

  // Preferred side first, then its opposite, then bottom as a general default.
  const order: TourPlacement[] =
    placement === 'top' ? ['top', 'bottom'] :
    placement === 'left' ? ['left', 'right'] :
    placement === 'right' ? ['right', 'left'] :
    ['bottom', 'top'];
  const side = order.find((s) => fits[s as keyof typeof fits]) ?? 'bottom';

  let top: number;
  let left: number;
  if (side === 'top' || side === 'bottom') {
    top = side === 'top' ? rect.top - size.height - GAP : rect.bottom + GAP;
    left = rect.left + rect.width / 2 - size.width / 2;
  } else {
    left = side === 'left' ? rect.left - size.width - GAP : rect.right + GAP;
    top = rect.top + rect.height / 2 - size.height / 2;
  }

  // Clamp inside the viewport.
  top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - size.height - MARGIN));
  left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - size.width - MARGIN));
  return { top, left };
}

/**
 * Themed popover shown next to the spotlighted element. Presentational only —
 * `TourRunner` owns step state, navigation, and keyboard handling.
 */
const TourCoachmark: React.FC<TourCoachmarkProps> = ({
  step, index, total, rect, onBack, onNext, onRequestSkip,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Bumped on viewport resize/zoom so the position recomputes even when the
  // target rect hasn't changed (e.g. centered placement with no target).
  const [viewportEpoch, setViewportEpoch] = useState(0);
  // Centered (no target) for a while → explain why, so the step isn't
  // describing something invisible. Delayed so it doesn't flash during a
  // normal resolve that's just taking a moment.
  const [showMissingNote, setShowMissingNote] = useState(false);
  useEffect(() => {
    if (rect) {
      setShowMissingNote(false);
      return;
    }
    const t = window.setTimeout(() => setShowMissingNote(true), 2500);
    return () => window.clearTimeout(t);
  }, [rect]);

  useEffect(() => {
    const onResize = () => setViewportEpoch((e) => e + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Measure our own size before positioning (placement depends on it).
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const size = { width: el.offsetWidth, height: el.offsetHeight };
    if (rect) {
      setPos(computePosition(rect, size, step.placement ?? 'bottom'));
    } else {
      setPos(computePosition(new DOMRect(0, 0, 0, 0), size, 'center'));
    }
  }, [rect, step, viewportEpoch]);

  const isLast = index === total - 1;

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-label={step.title}
      className="cgm-card p-4 w-80 max-w-[calc(100vw-16px)] shadow-2xl"
      data-tour-coachmark="true"
      style={{
        // Inline because the unlayered `.cgm-card { position: relative }` rule
        // beats Tailwind's layered `fixed` utility in the cascade.
        position: 'fixed',
        zIndex: 1002,
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        // Hide until measured+positioned to avoid a flash at (0,0).
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: 'auto',
        // `.cgm-card { transition: all }` would slide top/left between steps —
        // kill that and use the same opacity fadeIn as sidebars/modals.
        transition: 'none',
        animation: pos ? 'fadeIn 0.2s ease' : undefined,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onRequestSkip}
        className="absolute top-3 right-3 cgm-text-secondary hover:opacity-70 transition-opacity"
        aria-label="Skip all tours"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="text-[11px] uppercase tracking-wider cgm-text-secondary mb-1">
        Step {index + 1} of {total}
      </div>
      <h4 className="cgm-text-primary font-semibold pr-6">{step.title}</h4>
      <p className="cgm-text-secondary text-sm mt-1.5 leading-relaxed">{step.body}</p>
      {!rect && showMissingNote && (
        <p className="cgm-text-secondary text-xs mt-2 italic leading-relaxed">
          We couldn't spot this on your screen right now — it may need data or
          setup first. Use Next to keep going.
        </p>
      )}

      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={onRequestSkip}
          className="text-xs cgm-text-secondary hover:opacity-70 underline underline-offset-2 transition-opacity"
          title="Dismiss this tour and stop auto-starting other tutorials"
        >
          Skip all tours
        </button>
        <div className="flex items-center gap-2">
          {index > 0 && (
            <button type="button" onClick={onBack} className="cgm-button-flat py-1.5 px-3 text-sm">
              Back
            </button>
          )}
          <button type="button" onClick={onNext} className="cgm-button py-1.5 px-4 text-sm">
            {isLast ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourCoachmark;
